import { Inbox } from "lucide-react";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Panel } from "@/components/ui/Panel";
import { cn } from "@/lib/utils";
import { UNASSIGNED_TITLE, initialsOf } from "./paths";

/**
 * A client's logo, or its initials where it has none or the picture will not
 * load.
 *
 * The link to a logo is signed, and it dies early when the credentials that
 * signed it rotate. So a picture that fails asks for the list once more, which
 * signs a fresh link, and a second failure settles on the initials rather than
 * showing a broken image.
 */
export function ClientLogo({
  name,
  logoUrl,
  onBroken,
  className,
  children,
}: {
  name: string;
  logoUrl: string | null;
  /** Asks for a fresh link; called at most once, however often the picture fails. */
  onBroken?: () => void;
  className?: string;
  /** Drawn in place of the initials, for a card that is not a client's. */
  children?: ReactNode;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const askedRef = useRef(false);
  const showsPicture = logoUrl !== null && logoUrl !== failed;

  return (
    <Panel
      as="span"
      pad="none"
      aria-hidden
      className={cn(
        "grid size-[78px] shrink-0 place-items-center overflow-hidden",
        showsPicture
          ? "p-[7px]"
          : "bg-muted text-[24px] font-bold tracking-[0.01em] text-primary",
        className,
      )}
    >
      {showsPicture ? (
        <img
          src={logoUrl}
          alt=""
          className="block size-full object-contain"
          onError={() => {
            setFailed(logoUrl);
            if (!askedRef.current) {
              askedRef.current = true;
              onBroken?.();
            }
          }}
        />
      ) : (
        (children ?? initialsOf(name))
      )}
    </Panel>
  );
}

/** The picture standing for the drafts under no client: a tray, where a client has its logo. */
export function UnassignedMark() {
  return (
    <ClientLogo name={UNASSIGNED_TITLE} logoUrl={null}>
      <Inbox className="size-7" />
    </ClientLogo>
  );
}
